import { useEffect } from 'react';
import type { Characteristic, MatrixValueCharacteristic } from '../../schema';
import { useEvaluated, useEvaluatedMap } from '../../eval/EvalContext';
import { useComponent } from '../../state/ComponentContext';
import { useTranslation } from '../../i18n';
import { formatCalculatedValue } from './formatCalculatedValue';
import { selectMatrixTable } from '../../eval/evaluator';

/**
 * Read-only display of the matrix result — the looked-up cell value, not
 * the matrix table itself. The full table + dimensions + the RESOLVED
 * X / Y inputs go to `console.debug` so support can immediately see why
 * a lookup is returning null (wrong axis input vs missing CSV coverage).
 * Customers never see any of that.
 */
export function MatrixValueView({ characteristic }: { characteristic: MatrixValueCharacteristic }) {
    const { locale } = useTranslation();
    const result = useEvaluated(characteristic.id);
    const component = useComponent();
    const evaluated = useEvaluatedMap();

    // Resolve the alt_title refs to the actual input values that the
    // matrix sees right now. Mirrors evalMatrix's lookup so admins debugging
    // "why null?" don't have to read source.
    const xRef = stripDollars(characteristic.matrixDimensionX);
    const yRef = stripDollars(characteristic.matrixDimensionY);
    const xInput = xRef ? evaluatedFor(xRef, component.groups.flatMap((g) => g.characteristics), evaluated) : null;
    const yInput = yRef ? evaluatedFor(yRef, component.groups.flatMap((g) => g.characteristics), evaluated) : null;
    const controlRef = stripDollars(characteristic.controlMatrix);
    const controlInput = controlRef ? evaluatedFor(controlRef, component.groups.flatMap((g) => g.characteristics), evaluated) : null;
    const table = selectMatrixTable(characteristic, controlInput);

    useEffect(() => {
        // Flat payload — DevTools renders top-level primitives inline so
        // the lookup state ("did the X axis resolve?") is visible without
        // expanding nested objects.
        const data = table?.data;
        console.debug(
            '[configento] matrix result',
            characteristic.altTitle || characteristic.id,
            {
                table: table?.name,
                controlInput,
                xRef: characteristic.matrixDimensionX,
                xOp: characteristic.matrixOperatorX,
                xInput,
                xCandidates: data?.[0]?.slice(1),
                yRef: characteristic.matrixDimensionY,
                yOp: characteristic.matrixOperatorY,
                yInput,
                yCandidates: data?.slice(1).map((row) => row?.[0]),
                rows: data?.length ?? 0,
                cols: data?.[0]?.length ?? 0,
                xRefResolved: xRef !== undefined ? Boolean(xRef) : false,
                yRefResolved: yRef !== undefined ? Boolean(yRef) : false,
                xCharacteristicFound:
                    xRef !== undefined
                        ? Boolean(component.groups.flatMap((g) => g.characteristics).find((c) => c.altTitle === xRef))
                        : false,
                yCharacteristicFound:
                    yRef !== undefined
                        ? Boolean(component.groups.flatMap((g) => g.characteristics).find((c) => c.altTitle === yRef))
                        : false,
                result,
            },
        );
    }, [
        component,
        xRef,
        yRef,
        characteristic.id,
        characteristic.altTitle,
        characteristic.matrix,
        characteristic.matrixDimensionX,
        characteristic.matrixOperatorX,
        characteristic.matrixDimensionY,
        characteristic.matrixOperatorY,
        xInput,
        yInput,
        controlInput,
        table,
        result,
    ]);

    return (
        <span className="cfg-calc-value" data-configento-eval-result>
            {formatCalculatedValue(result, characteristic.decimalPlace, locale)}
        </span>
    );
}

function stripDollars(ref: string | undefined): string | undefined {
    if (!ref) return undefined;
    return ref.replace(/^\$+/, '');
}

function evaluatedFor(
    altTitle: string,
    characteristics: ReadonlyArray<Characteristic>,
    evaluated: ReturnType<typeof useEvaluatedMap>,
): number | string | null {
    const target = characteristics.find((c) => c.altTitle === altTitle);
    if (!target) return null;
    return evaluated.get(target.id) ?? null;
}
